'use client';

import { cn } from '@/lib/utils';
import { useMemo, useState } from 'react';

type Mode = 'between' | 'add';

function countBusinessDays(start: Date, end: Date) {
  let count = 0;
  const cursor = new Date(start);
  while (cursor <= end) {
    const day = cursor.getDay();
    if (day !== 0 && day !== 6) count++;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}

export function DateCalculator() {
  const [mode, setMode] = useState<Mode>('between');

  const [startDate, setStartDate] = useState('2026-01-01');
  const [endDate, setEndDate] = useState('2026-12-31');
  const [businessOnly, setBusinessOnly] = useState(false);

  const [baseDate, setBaseDate] = useState('2026-01-01');
  const [amount, setAmount] = useState('30');
  const [unit, setUnit] = useState<'days' | 'weeks' | 'months'>('days');
  const [direction, setDirection] = useState<'add' | 'subtract'>('add');

  const betweenResult = useMemo(() => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
    const [from, to] = start <= end ? [start, end] : [end, start];
    const totalDays = Math.round((to.getTime() - from.getTime()) / 86_400_000);
    return businessOnly ? countBusinessDays(from, to) - 1 : totalDays;
  }, [startDate, endDate, businessOnly]);

  const addResult = useMemo(() => {
    const base = new Date(baseDate);
    const n = Number.parseInt(amount, 10);
    if (Number.isNaN(base.getTime()) || !Number.isFinite(n)) return null;
    const signed = direction === 'add' ? n : -n;
    const result = new Date(base);
    if (unit === 'days') result.setDate(result.getDate() + signed);
    if (unit === 'weeks') result.setDate(result.getDate() + signed * 7);
    if (unit === 'months') {
      // setMonth() overflows into the next month when the target month is
      // shorter than the original day-of-month (e.g. Jan 31 + 1 month lands
      // on Mar 2-3, not Feb 28) — clamp back to the target month's last day.
      const originalDay = result.getDate();
      result.setMonth(result.getMonth() + signed);
      if (result.getDate() !== originalDay) result.setDate(0);
    }
    return result;
  }, [baseDate, amount, unit, direction]);

  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex gap-2">
        {(
          [
            { id: 'between', label: 'Days between dates' },
            { id: 'add', label: 'Add / subtract days' },
          ] as const
        ).map((m) => (
          <button
            key={m.id}
            type="button"
            onClick={() => setMode(m.id)}
            className={cn(
              'rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors',
              mode === m.id
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            {m.label}
          </button>
        ))}
      </div>

      {mode === 'between' ? (
        <div className="mt-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-muted-foreground">From</span>
              <input
                type="date"
                name="date-range-start"
                autoComplete="off"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1 h-10 w-full rounded-lg border border-input bg-background px-3 text-foreground outline-none focus:border-ring focus:ring-2 focus:ring-ring/30"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-muted-foreground">To</span>
              <input
                type="date"
                name="date-range-end"
                autoComplete="off"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-1 h-10 w-full rounded-lg border border-input bg-background px-3 text-foreground outline-none focus:border-ring focus:ring-2 focus:ring-ring/30"
              />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              name="business-days-only"
              checked={businessOnly}
              onChange={(e) => setBusinessOnly(e.target.checked)}
              className="size-4 rounded border-input accent-primary"
            />
            Count business days only
          </label>

          <div className="rounded-xl bg-primary/10 px-4 py-4">
            <p className="text-xs font-medium text-muted-foreground">
              {businessOnly ? 'Business days' : 'Total days'}
            </p>
            <p className="text-3xl font-bold text-primary">{betweenResult ?? '—'}</p>
          </div>
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <label className="block">
            <span className="text-xs font-medium text-muted-foreground">Start date</span>
            <input
              type="date"
              name="date-math-base"
              autoComplete="off"
              value={baseDate}
              onChange={(e) => setBaseDate(e.target.value)}
              className="mt-1 h-10 w-full rounded-lg border border-input bg-background px-3 text-foreground outline-none focus:border-ring focus:ring-2 focus:ring-ring/30"
            />
          </label>
          <div className="grid grid-cols-3 gap-3">
            <select
              name="date-math-direction"
              value={direction}
              onChange={(e) => setDirection(e.target.value as 'add' | 'subtract')}
              className="h-10 rounded-lg border border-input bg-background px-2 text-sm text-foreground"
            >
              <option value="add">Add</option>
              <option value="subtract">Subtract</option>
            </select>
            <input
              type="number"
              name="date-math-amount"
              autoComplete="off"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="h-10 rounded-lg border border-input bg-background px-3 text-foreground"
            />
            <select
              name="date-math-unit"
              value={unit}
              onChange={(e) => setUnit(e.target.value as 'days' | 'weeks' | 'months')}
              className="h-10 rounded-lg border border-input bg-background px-2 text-sm text-foreground"
            >
              <option value="days">Days</option>
              <option value="weeks">Weeks</option>
              <option value="months">Months</option>
            </select>
          </div>

          <div className="rounded-xl bg-primary/10 px-4 py-4">
            <p className="text-xs font-medium text-muted-foreground">Result</p>
            <p className="text-2xl font-bold text-primary">
              {addResult
                ? addResult.toLocaleString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : '—'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
